import { Component, OnInit, Input, ViewContainerRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TeamService } from 'src/app/shared/services/team.service';
import { Position } from 'src/app/shared/models/team/position';
import { TeamWrite } from 'src/app/shared/models/team/teamWrite';
import { PositionWrite } from 'src/app/shared/models/team/positionWrite';
import { NotEmptyValidator } from 'src/app/shared/validators/not-empty.validator';
import { NoPipeValidator } from 'src/app/shared/validators/no-pipe.validator';
import { AdminAutomationOdataFilterComponent } from 'src/app/admin/admin-automation-odata-filter/admin-automation-odata-filter.component';
import { PeopleFilterService } from 'src/app/people/people-filter.service';
import { SavedFilter } from 'src/app/shared/components/odata-filter/models/saved-filter';
import { SelectedFilter } from 'src/app/shared/components/odata-filter/models/selected-filter';
import { ViewContext } from 'src/app/shared/components/odata-filter/models/view-context';
import { FilterGroup } from 'src/app/shared/components/odata-filter/models/odata-filter';
import { AuthService } from 'src/app/auth/auth.service';

interface PositionFormControls {
  title: FormControl<string>;
  seatCount: FormControl<number>;
  description: FormControl<string>;
  arePeopleSyncedToTeamManager: FormControl<boolean>;
}

@Component({
  selector: 'position-details',
  templateUrl: 'position-details.component.html',
  styleUrls: ['./position-details.component.scss'],
  standalone: false
})
export class PositionDetailsComponent implements OnInit {
  positionForm: FormGroup<PositionFormControls>;
  @Input() position: Position;
  teamWrite: TeamWrite;

  @ViewChild(AdminAutomationOdataFilterComponent)
  eligibilityFilter: AdminAutomationOdataFilterComponent;
  appliedFilters: SelectedFilter[] = [];
  readonly allowedFilterGroups: FilterGroup[] = [
    FilterGroup.PersonCustomFields,
    FilterGroup.PersonSkills
  ];
  readonly allowedFilterDisplayNames: string[] = [
    'Active Certifications',
    'Credentials',
    'State',
    'Status'
  ];

  constructor(
    private teamService: TeamService,
    private formBuilder: FormBuilder,
    public peopleFilterService: PeopleFilterService,
    private authService: AuthService,
    public dialogRef: MatDialogRef<PositionDetailsComponent>
  ) {}

  static openPositionDialog(
    dialog: MatDialog,
    viewContainerRef: ViewContainerRef,
    position?: Position
  ) {
    const positionDialogRef = dialog.open(PositionDetailsComponent, {
      width: '900px',
      viewContainerRef: viewContainerRef
    });

    if (position) {
      positionDialogRef.componentInstance.position = position;
    }
  }

  get isStaff(): boolean {
    return this.authService.isStaff();
  }

  ngOnInit() {
    this.buildPositionForm();

    if (this.isStaff && this.position?.eligibilityFilters?.length) {
      this.appliedFilters = SavedFilter.parseToAppliedFilters(
        this.position.eligibilityFilters,
        ViewContext.People
      );
    }
  }

  buildPositionForm() {
    this.positionForm = this.formBuilder.group<PositionFormControls>({
      title: this.formBuilder.control(this.position ? this.position.title : '', [
        NotEmptyValidator('Position title is required'),
        NoPipeValidator()
      ]),
      seatCount: this.formBuilder.control(this.position ? this.position.seatCount : 1, [
        Validators.required,
        Validators.min(1)
      ]),
      description: this.formBuilder.control(
        this.position ? this.position.description : '',
        Validators.maxLength(500)
      ),
      arePeopleSyncedToTeamManager: this.formBuilder.control(
        this.position ? this.position.arePeopleSyncedToTeamManager : false
      )
    });
  }

  async savePosition() {
    const model = this.assignFormValuesToPosition();
    if (!model) {
      return;
    }
    await this.teamService.updatePosition(model);
    this.dialogRef.close('yes');
  }

  async createPosition() {
    const model = this.assignFormValuesToPosition();
    if (!model) {
      return;
    }
    await this.teamService.createPosition(model);
    this.dialogRef.close('yes');
  }

  assignFormValuesToPosition(): PositionWrite | null {
    const { title, description, seatCount, arePeopleSyncedToTeamManager } = this.positionForm.value;

    let eligibilityFilters: string[] = this.position ? this.position.eligibilityFilters : [];
    let eligibilityODataQuery: string = this.position ? this.position.eligibilityODataQuery : null;

    if (this.isStaff && this.eligibilityFilter) {
      const model = this.eligibilityFilter.getFilterModel();
      if (model === null) {
        return null;
      }
      eligibilityFilters = model.oDataFilters;
      eligibilityODataQuery = model.oDataQuery;
    }

    return new PositionWrite(
      title,
      seatCount,
      arePeopleSyncedToTeamManager,
      this.position ? this.position.id : null,
      description,
      eligibilityFilters,
      eligibilityODataQuery
    );
  }
}
